import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Skeleton,
  Button,
  Popover,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Schedule as DurationIcon,
  LocationOn as LocationIcon,
  AttachMoney as BudgetIcon,
  Star as FeaturedIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';
import api from '../config/api';
import { THEME_COLORS } from '../constants/theme';

const categoryConfig = {
  sport:           { label: 'Sport',           color: '#e53935', emoji: '🏆' },
  music:           { label: 'Music',           color: '#8e24aa', emoji: '🎵' },
  cultural:        { label: 'Cultural',        color: '#6d4c41', emoji: '🎭' },
  food:            { label: 'Food',            color: '#ef6c00', emoji: '🍴' },
  theme_park:      { label: 'Theme Park',      color: '#1e88e5', emoji: '🎢' },
  city_experience: { label: 'City',            color: '#00897b', emoji: '🏙️' },
  layover:         { label: 'Layover',         color: '#546e7a', emoji: '✈️' },
  nature:          { label: 'Nature',          color: '#43a047', emoji: '🌿' },
  festival:        { label: 'Festival',        color: '#d81b60', emoji: '🎉' },
  expo:            { label: 'Expo',            color: '#5c6bc0', emoji: '🎪' },
  parade:          { label: 'Parade',          color: '#f4511e', emoji: '🎊' },
  seasonal:        { label: 'Seasonal',        color: '#26a69a', emoji: '🌸' },
  cruise:          { label: 'Cruise',          color: '#0277bd', emoji: '🚢' },
};

const EventGuides = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      params.set('limit', '50');

      const response = await api.get(`/event-guides?${params.toString()}`);
      setGuides(response.data.guides || []);
    } catch (err) {
      console.error('Error fetching event guides:', err);
      setError('Failed to load event guides. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category) params.set('category', category);
    setSearchParams(params, { replace: true });
  }, [search, category, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGuides();
  };

  const [filterAnchor, setFilterAnchor] = useState(null);
  const filterOpen = Boolean(filterAnchor);

  const handleCategoryToggle = (cat) => {
    setCategory(prev => prev === cat ? '' : cat);
    setFilterAnchor(null);
  };

  const featuredGuides = guides.filter(g => g.is_featured);
  const regularGuides = guides.filter(g => !g.is_featured);

  return (
    <>
      <SEOHead
        title="Event & Experience Guides"
        description="Curated travel guides for the world's best events, festivals, and experiences. From Wimbledon to Coachella, plan your next adventure with insider tips on where to stay, eat, and what to do."
        canonical="/event-guides"
        keywords="event guides, travel guides, festival guide, wimbledon guide, coachella guide, dubai layover, tokyo travel, oktoberfest, burning man, disney world"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Event & Experience Guides',
          description: 'Curated travel guides for the world\'s best events, festivals, and experiences.',
          url: 'https://dejavunomad.com/event-guides',
          publisher: {
            '@type': 'Organization',
            name: 'Déjà Vu Nomad',
            url: 'https://dejavunomad.com',
          },
        }}
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${THEME_COLORS.primary.main} 0%, ${THEME_COLORS.primary.dark || '#3d0f0f'} 100%)`,
          color: 'white',
          py: { xs: 6, md: 10 },
          mb: 4,
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', md: '2.8rem' } }}
          >
            Event & Experience Guides
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 4, opacity: 0.9, maxWidth: 700, fontWeight: 400, fontSize: { xs: '1rem', md: '1.2rem' } }}
          >
            Curated insider guides for the world's best events, festivals, and travel experiences. Everything you need to plan the perfect trip.
          </Typography>

          {/* Search */}
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{ maxWidth: 550 }}
          >
            <TextField
              fullWidth
              placeholder={t('Search events, destinations...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: 'white',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '& fieldset': { border: 'none' },
                },
              }}
            />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mb: 8 }}>
        {/* Category Filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, flexWrap: 'wrap' }}>
          <Badge
            color="primary"
            variant="dot"
            invisible={!category}
          >
            <Button
              variant={filterOpen ? 'contained' : 'outlined'}
              startIcon={<FilterIcon />}
              endIcon={<ArrowDownIcon sx={{ transition: 'transform 0.2s', transform: filterOpen ? 'rotate(180deg)' : 'none' }} />}
              onClick={(e) => setFilterAnchor(filterOpen ? null : e.currentTarget)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: 1,
                borderColor: 'divider',
                color: filterOpen ? 'white' : 'text.primary',
                bgcolor: filterOpen ? THEME_COLORS.primary.main : 'transparent',
                '&:hover': { borderColor: 'text.primary', bgcolor: filterOpen ? THEME_COLORS.primary.main : 'action.hover' },
              }}
            >
              Category
            </Button>
          </Badge>

          {category && (
            <Chip
              label={`${categoryConfig[category]?.emoji} ${categoryConfig[category]?.label}`}
              onDelete={() => setCategory('')}
              deleteIcon={<CloseIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: `${categoryConfig[category]?.color}14`,
                color: categoryConfig[category]?.color,
                border: '1.5px solid',
                borderColor: `${categoryConfig[category]?.color}44`,
                '& .MuiChip-deleteIcon': { color: `${categoryConfig[category]?.color}88`, '&:hover': { color: categoryConfig[category]?.color } },
              }}
            />
          )}

          <Popover
            open={filterOpen}
            anchorEl={filterAnchor}
            onClose={() => setFilterAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                elevation: 4,
                sx: {
                  borderRadius: 3,
                  mt: 1,
                  p: 2.5,
                  maxWidth: 520,
                  border: '1px solid',
                  borderColor: 'divider',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                Filter by Category
              </Typography>
              {category && (
                <Button
                  size="small"
                  onClick={() => { setCategory(''); setFilterAnchor(null); }}
                  startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 'auto', px: 1 }}
                >
                  Clear
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(categoryConfig).map(([key, cfg]) => {
                const isActive = category === key;
                return (
                  <Chip
                    key={key}
                    label={`${cfg.emoji} ${cfg.label}`}
                    clickable
                    onClick={() => handleCategoryToggle(key)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      borderRadius: 2,
                      px: 0.5,
                      height: 36,
                      border: '1.5px solid',
                      transition: 'all 0.15s ease',
                      ...(isActive
                        ? {
                            bgcolor: `${cfg.color}18`,
                            borderColor: cfg.color,
                            color: cfg.color,
                            boxShadow: `0 0 0 1px ${cfg.color}33`,
                            '&:hover': { bgcolor: `${cfg.color}28` },
                          }
                        : {
                            bgcolor: 'transparent',
                            borderColor: 'divider',
                            color: 'text.primary',
                            '&:hover': { borderColor: cfg.color, bgcolor: `${cfg.color}08`, color: cfg.color },
                          }),
                    }}
                  />
                );
              })}
            </Box>
          </Popover>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Featured Section */}
        {!category && !search && featuredGuides.length > 0 && (
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FeaturedIcon sx={{ color: '#f59e0b' }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Featured Guides
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <Grid item xs={12} sm={6} md={3} key={`skel-f-${i}`}>
                      <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
                    </Grid>
                  ))
                : featuredGuides.map((guide) => (
                    <Grid item xs={12} sm={6} md={3} key={guide.id}>
                      <EventCard guide={guide} onClick={() => navigate(`/event-guides/${guide.slug}`)} />
                    </Grid>
                  ))}
            </Grid>
          </Box>
        )}

        {/* All Guides */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {category ? `${categoryConfig[category]?.emoji || ''} ${categoryConfig[category]?.label || category} Guides` : 'All Guides'}
        </Typography>

        <Grid container spacing={3}>
          {loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={`skel-${i}`}>
                  <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
                </Grid>
              ))
            : (category || search ? guides : regularGuides).length === 0 ? (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                    <Typography variant="h6">No guides found</Typography>
                    <Typography variant="body2">Try adjusting your filters or search term</Typography>
                  </Box>
                </Grid>
              )
            : (category || search ? guides : regularGuides).map((guide) => (
                <Grid item xs={12} sm={6} md={3} key={guide.id}>
                  <EventCard guide={guide} onClick={() => navigate(`/event-guides/${guide.slug}`)} />
                </Grid>
              ))}
        </Grid>
      </Container>
    </>
  );
};

const EventCard = ({ guide, onClick }) => {
  const catCfg = categoryConfig[guide.category] || { label: guide.category, color: '#607d8b', emoji: '📌' };

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: 3,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
          borderColor: THEME_COLORS.primary.main,
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height={180}
          image={guide.cover_image || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80'}
          alt={guide.title}
          onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80'; }}
          sx={{ objectFit: 'cover' }}
        />
        {/* Category badge */}
        <Chip
          label={`${catCfg.emoji} ${catCfg.label}`}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            bgcolor: catCfg.color,
            color: 'white',
            fontWeight: 700,
            fontSize: '0.7rem',
          }}
        />
        {!!guide.is_featured && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              bgcolor: '#f59e0b',
              color: 'white',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FeaturedIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3 }}>
          {guide.title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" noWrap>
            {guide.location_name}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <DurationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {guide.recommended_days} {guide.recommended_days === 1 ? 'day' : 'days'}
          </Typography>
        </Box>

        {(guide.budget_estimate_low || guide.budget_estimate_high) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
            <BudgetIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {guide.budget_currency || 'USD'} {guide.budget_estimate_low ? Math.round(guide.budget_estimate_low).toLocaleString() : '?'}
              {' – '}
              {guide.budget_estimate_high ? Math.round(guide.budget_estimate_high).toLocaleString() : '?'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EventGuides;
